// deps
import { useContext, useEffect, useRef, useState, useCallback } from "react";

// Contexts
import { CartOverlaysContext } from "@checkout-core/contexts/CartOverlaysContext";

// Types
import { GlobalOverlayPriority, LottieIds, OverlayType } from "@swiggy-private/cart-types";

export interface UseToingHeaderBannerAnimationParams {
    shouldRegister: boolean;
}

export interface UseToingHeaderBannerAnimationReturn {
    shouldPlayAnimation: boolean;
    onAnimationComplete: VoidFunction;
}

export const useToingHeaderBannerAnimation = ({
    shouldRegister,
}: UseToingHeaderBannerAnimationParams): UseToingHeaderBannerAnimationReturn => {
    const { registerOverlays, enabledOverlayId, onCloseOverlay } = useContext(CartOverlaysContext);

    const isRegisteredRef = useRef(false);
    const [hasAnimationStarted, setHasAnimationStarted] = useState(false);

    const onCloseOverlayRef = useRef(onCloseOverlay);
    onCloseOverlayRef.current = onCloseOverlay;

    useEffect(() => {
        if (!shouldRegister || isRegisteredRef.current) {
            return;
        }

        registerOverlays({
            overlayType: OverlayType.LOTTIE,
            priority: GlobalOverlayPriority.LOTTIE_ANIMATION,
            id: LottieIds.TOING_HEADER_BANNER_ANIMATION,
        });

        isRegisteredRef.current = true;
    }, [shouldRegister, registerOverlays]);

    useEffect(() => {
        if (enabledOverlayId === LottieIds.TOING_HEADER_BANNER_ANIMATION && !hasAnimationStarted) {
            setHasAnimationStarted(true);
        }
    }, [enabledOverlayId, hasAnimationStarted]);

    useEffect(() => {
        return () => {
            if (isRegisteredRef.current) {
                onCloseOverlayRef.current();
            }
        };
    }, []);

    const onAnimationComplete = useCallback(() => {
        if (enabledOverlayId === LottieIds.TOING_HEADER_BANNER_ANIMATION) {
            onCloseOverlay();
        }
    }, [enabledOverlayId, onCloseOverlay]);

    return {
        shouldPlayAnimation: shouldRegister && enabledOverlayId === LottieIds.TOING_HEADER_BANNER_ANIMATION,
        onAnimationComplete,
    };
};
